import { CircularProgress } from "@mui/material";
import { Button } from "components/Button/Button";
import CommentCountBadge from "components/CommentCountBadge";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { ImageIconComponent, imageProps } from "components/ImageIcon/ImageIcon";
import NotificationService from "components/Notification/Notification.service";
import Tooltip from "components/Tooltip/Tooltip";
import { initialFilter, initialTab } from "containers/AdvancedSearch/constants";
import { AdvancedSearchTabsType } from "containers/AdvancedSearch/interfaces";
import { useAdvancedFilter } from "containers/AdvancedSearch/useAdvancedFilter";
import { useQueryParams } from "containers/ExecutionFilter/utils";
import { InTrashBadge } from "containers/Resources/components/Badges/Badges";
import badgeStyle from "containers/Resources/components/Badges/Badges.module.scss";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Popover, Whisper } from "rsuite";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { mapScreensToPaths } from "utils/RouterManager/utils";
import { getRelativeUrl, opensInNewTab, prefersIdInUrl } from "utils/service";
import { FeAllType, FeRecordType } from "config/config";
import useLocalStorage from "utils/useLocalStorage";
import {
    findTermInActivities,
    findTermInAdvancedSearchResources,
    getSearchField,
} from "utils/utils";
import { fetchFullTextSearch, FullTextSearchResult } from "./fullTextSearch.service";
import "./SearchBar.scss";

const SearchBar = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { queryParams } = useQueryParams();
    const searchBarInputRef = useRef<HTMLInputElement>(null);
    const popoverTriggerRef = useRef<any>();
    const [selectedLine, setSelectedLine] = useState(0);
    const key = "quickSearch";
    const [recentSearches, setRecentSearches] = useLocalStorage("recentSearches", []);
    const searchResultsSelector = (state) => state?.dataStore?.[key]?.record_list;
    const searchResults = useSelector(searchResultsSelector);
    const abortControllerRef = useRef<AbortController | null>(null);
    const searchRequestIdRef = useRef(0);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResultsTerm, setSearchResultsTerm] = useState<string | null>(null);
    // Full-text search (GET /fulltext-search): a second, content-aware result
    // section alongside the name-based quick search above.
    const fullTextAbortRef = useRef<AbortController | null>(null);
    const fullTextRequestIdRef = useRef(0);
    const [isFullTextLoading, setIsFullTextLoading] = useState(false);
    const [fullTextResults, setFullTextResults] = useState<FullTextSearchResult[] | null>(null);
    const [fullTextTerm, setFullTextTerm] = useState<string | null>(null);
    const resultsLimit = 10;
    const currentSearchTerm = searchBarInputRef.current?.value ?? "";
    const showSearchResults =
        Boolean(searchResults) &&
        Boolean(currentSearchTerm) &&
        searchResultsTerm === currentSearchTerm;
    // Ids already shown in the name-search section, so we don't list them twice.
    const nameResultIds = new Set<string>(
        showSearchResults ? searchResults?.record_list?.map((r) => r?.id) ?? [] : []
    );
    const dedupedFullTextResults = (
        fullTextTerm === currentSearchTerm ? fullTextResults ?? [] : []
    ).filter((r) => r?.id && !nameResultIds.has(r.id));
    const showFullTextResults = Boolean(currentSearchTerm) && dedupedFullTextResults.length > 0;
    // Preserve backend rank order while grouping by record_type for display.
    const fullTextGroups: [string, FullTextSearchResult[]][] = [];
    dedupedFullTextResults.forEach((r) => {
        const group = fullTextGroups.find(([type]) => type === r.record_type);
        if (group) {
            group[1].push(r);
        } else {
            fullTextGroups.push([r.record_type, [r]]);
        }
    });
    const totalLinesCount =
        1 + // advanced search
        recentSearches.length +
        (showSearchResults && searchResults?.record_count
            ? Math.min(resultsLimit, searchResults?.record_count) +
              (searchResults?.record_count > resultsLimit ? 1 : 0)
            : 0) +
        (showFullTextResults ? dedupedFullTextResults.length : 0);

    const closePopover = () => popoverTriggerRef.current?.close();
    const openPopover = () => popoverTriggerRef.current?.open();

    const addRecentSearch = (term) => {
        if (!term) return;
        const otherSearches = recentSearches.filter((existingTerm) => existingTerm !== term);
        setRecentSearches([term, ...otherSearches].slice(0, 5));
    };

    const {
        onChangeHandlers: { onSearchQueryChange, onResourceTypeChange },
        seedAdvancedSearchFilter,
    } = useAdvancedFilter(queryParams, history);

    const setInitialFilterHandler = useCallback(
        (term: string, searchRecordType?: boolean) => {
            const getResourceTypeFilter = (term) => {
                if (searchRecordType) {
                    return { resource_type: [term?.toUpperCase()] };
                } else {
                    return {};
                }
            };

            const nameFilter = searchRecordType ? {} : { name: term };

            const activeTab: AdvancedSearchTabsType =
                findTermInActivities(term) && (term === "execution" || term === "message")
                    ? (`${term}s` as AdvancedSearchTabsType)
                    : initialTab;

            const filter = term
                ? { ...initialFilter, ...nameFilter, ...getResourceTypeFilter(term) }
                : initialFilter;

            seedAdvancedSearchFilter(filter, activeTab, "push");
        },
        [seedAdvancedSearchFilter]
    );
    const dispatchSearch = useCallback(
        (term: string) => {
            abortControllerRef.current?.abort();

            if (!term) {
                searchRequestIdRef.current += 1;
                setIsLoading(false);
                setSearchResultsTerm(null);
                return;
            }

            abortControllerRef.current = new AbortController();
            const requestId = (searchRequestIdRef.current += 1);
            setIsLoading(true);
            setSearchResultsTerm(null);

            const filterStr = JSON.stringify(getSearchField({ term, addRecordTypeFilter: true }));
            new Promise((resolve, reject) => {
                dispatch({
                    promise: { resolve, reject },
                    type: dataStoreAction.START_FETCH_DATA,
                    key,
                    signal: abortControllerRef.current?.signal,
                    params: {
                        query: `query($filter: String, $order: String, $limit: Int) {
                        record_list(filter:$filter,order:$order,limit:$limit){
                            record_count
                            record_list {
                                id
                                name
                                comment_count
                                record_type
                                resolved_icon
                                resolved_object_template_id
                                created_at
                                project_id
                                project_id_project{
                                    name
                                }
                                deleted_at
                            }
                        }
                    }`,
                        variables: {
                            filter: filterStr,
                            order: "-deleted_at, +name",
                            limit: resultsLimit,
                        },
                        allow_deleted: true,
                        allow_normal: true,
                    },
                });
            })
                .then((data) => {
                    if (requestId !== searchRequestIdRef.current) {
                        return;
                    }
                    if (data) {
                        setSearchResultsTerm(term);
                    }
                })
                .catch(() => undefined)
                .finally(() => {
                    if (requestId === searchRequestIdRef.current) {
                        setIsLoading(false);
                    }
                });
        },
        [dispatch, resultsLimit]
    );

    const dispatchSearchDelayed = useMemo(
        () => debounce((term: string) => dispatchSearch(term), 100),
        [dispatchSearch]
    );

    const dispatchFullTextSearch = useCallback((term: string) => {
        fullTextAbortRef.current?.abort();

        if (!term) {
            fullTextRequestIdRef.current += 1;
            setIsFullTextLoading(false);
            setFullTextResults(null);
            setFullTextTerm(null);
            return;
        }

        fullTextAbortRef.current = new AbortController();
        const requestId = (fullTextRequestIdRef.current += 1);
        setIsFullTextLoading(true);

        fetchFullTextSearch(term, {
            limit: resultsLimit,
            signal: fullTextAbortRef.current.signal,
        })
            .then((results) => {
                if (requestId !== fullTextRequestIdRef.current) {
                    return;
                }
                setFullTextResults(results);
                setFullTextTerm(term);
            })
            .catch(() => undefined)
            .finally(() => {
                if (requestId === fullTextRequestIdRef.current) {
                    setIsFullTextLoading(false);
                }
            });
    }, []);

    const dispatchFullTextSearchDelayed = useMemo(
        () => debounce((term: string) => dispatchFullTextSearch(term), 250),
        [dispatchFullTextSearch]
    );

    useEffect(
        () => () => {
            dispatchSearchDelayed.cancel();
            abortControllerRef.current?.abort();
            dispatchFullTextSearchDelayed.cancel();
            fullTextAbortRef.current?.abort();
        },
        [dispatchSearchDelayed, dispatchFullTextSearchDelayed]
    );

    let indexCounter = 0;
    const handleCurrentLineAndGetClass = (action) => {
        indexCounter += 1;
        return `line ${indexCounter === selectedLine ? "selected" : ""}`;
    };

    const resetSearchField = () => {
        if (searchBarInputRef?.current?.value) {
            searchBarInputRef.current.value = "";
        }
    };

    // Copy a result row's exact record name to the clipboard. Reads from the
    // source data (searchResult.name), not the rendered DOM text, so names with
    // consecutive spaces or other rendering transforms are copied literally.
    // preventDefault/stopPropagation so the surrounding row link does not navigate.
    const handleCopyName = (e, name?: string | null) => {
        e.preventDefault();
        e.stopPropagation();
        if (!name) {
            return;
        }
        navigator.clipboard
            ?.writeText(name)
            .then(() => NotificationService.success("Name copied to clipboard"))
            .catch(() => NotificationService.error("Could not copy name to clipboard"));
    };

    const [recordTypeTerm, setRecordTypeTerm] = useState<string>("");

    useEffect(() => {
        if (
            searchBarInputRef.current?.value &&
            findTermInAdvancedSearchResources(searchBarInputRef?.current?.value)
        ) {
            setRecordTypeTerm(searchBarInputRef.current.value);
        } else {
            setRecordTypeTerm("");
        }
    }, [searchBarInputRef.current?.value]);

    const openAdvancedSearch = ({ searchRecordType }: { searchRecordType?: boolean }) => {
        if (history.location.pathname !== mapScreensToPaths.advancedSearch) {
            setInitialFilterHandler(searchBarInputRef?.current?.value ?? "", searchRecordType);
        } else {
            if (searchRecordType) {
                onResourceTypeChange(recordTypeTerm?.toUpperCase());
            } else {
                onSearchQueryChange(searchBarInputRef.current?.value ?? "");
            }
        }
        resetSearchField();
        closePopover();
    };

    useEffect(() => {
        const keydownHandler = (e) => {
            if (e.ctrlKey && e.key === "e") {
                e.preventDefault();
                searchBarInputRef?.current?.focus();
            }
        };
        window.addEventListener("keydown", keydownHandler);
        return () => {
            window.removeEventListener("keydown", keydownHandler);
        };
    }, []);

    const resultsPanel = (
        <Popover className="searchPopover">
            {(isLoading || isFullTextLoading) && currentSearchTerm && (
                <div className="searchLoading">
                    <CircularProgress size={16} />
                    <span>Searching...</span>
                </div>
            )}
            {showSearchResults && (
                <>
                    <div className="title text-xs text-uppercase">
                        <span>
                            {searchResults?.record_count ? searchResults?.record_count : "no"}{" "}
                            Search result{searchResults?.record_count === 1 ? "" : "s"} for &quot;
                            {currentSearchTerm}&quot;
                        </span>
                    </div>
                    {searchResults?.record_list?.map((searchResult) => {
                        const path = prefersIdInUrl(searchResult?.record_type)
                            ? getRelativeUrl(searchResult?.record_type, searchResult?.id, "id")
                            : getRelativeUrl(searchResult?.record_type, searchResult?.name, "name");
                        const onClickFn = (e?: React.MouseEvent) => {
                            // Keep the popover (and the current search term) open
                            // when the result opens in a NEW tab (Cmd/Ctrl/Shift/
                            // middle-click): the current tab does not navigate, so
                            // the user can fan out further results into more tabs.
                            // A plain click navigates the current tab away, so we
                            // dismiss the popover and clear the field as before.
                            if (opensInNewTab(e)) {
                                return;
                            }
                            closePopover();
                            addRecentSearch(searchBarInputRef?.current?.value);
                            resetSearchField();
                        };
                        const uniqueKey = `searchBarResultsEntry-${searchResult?.id}`;
                        return (
                            <div className="searchResultRow" key={uniqueKey}>
                                <Link
                                    to={path}
                                    onClick={onClickFn}
                                    className={handleCurrentLineAndGetClass(() => {
                                        onClickFn();
                                        history.push(path);
                                    })}
                                >
                                    <div className="flex flex-column">
                                        <div className="flex items-center gap-2">
                                            <ImageIconComponent
                                                iconByType={searchResult?.record_type?.toLowerCase()}
                                                image={searchResult?.resolved_icon}
                                                {...imageProps(
                                                    searchResult?.record_type?.toLowerCase(),
                                                    searchResult?.id,
                                                    searchResult?.resolved_object_template_id
                                                )}
                                            />
                                            <span className="recordName">
                                                {searchResult?.name}
                                            </span>
                                            <CommentCountBadge count={searchResult?.comment_count} />
                                        </div>
                                        <div
                                            className={`${badgeStyle.stateChips} ${badgeStyle.stateChipsWrap} ${badgeStyle.quickSearchChips}`}
                                        >
                                            <InTrashBadge deletedAt={searchResult?.deleted_at} />
                                        </div>
                                    </div>
                                </Link>
                                {searchResult?.name ? (
                                    <Tooltip overlay="Copy name">
                                        <IconButton
                                            className="copyNameButton"
                                            icon={IconCollection.contentCopy}
                                            onClick={(e) => handleCopyName(e, searchResult?.name)}
                                        />
                                    </Tooltip>
                                ) : null}
                            </div>
                        );
                    })}
                </>
            )}
            {showFullTextResults && (
                <>
                    <div className="title text-xs text-uppercase">
                        <span>Full-text matches</span>
                    </div>
                    {fullTextGroups.map(([recordType, groupResults]) => (
                        <div key={`fullTextGroup-${recordType}`}>
                            <span className="title text-xs text-uppercase">
                                {recordType.replace(/_/g, " ").toLowerCase()}
                            </span>
                            {groupResults.map((searchResult) => {
                                const path =
                                    prefersIdInUrl(searchResult?.record_type) ||
                                    !searchResult?.name
                                        ? getRelativeUrl(
                                              searchResult?.record_type as FeAllType,
                                              searchResult?.id,
                                              "id"
                                          )
                                        : getRelativeUrl(
                                              searchResult?.record_type as FeAllType,
                                              searchResult?.name,
                                              "name"
                                          );
                                const onClickFn = (e?: React.MouseEvent) => {
                                    // Keep the popover open on a NEW-tab click
                                    // (Cmd/Ctrl/Shift/middle-click) so the user can
                                    // fan out more results; a plain click navigates
                                    // the current tab, so dismiss + clear as before.
                                    if (opensInNewTab(e)) {
                                        return;
                                    }
                                    closePopover();
                                    addRecentSearch(searchBarInputRef?.current?.value);
                                    resetSearchField();
                                };
                                const uniqueKey = `searchBarFullTextEntry-${searchResult?.id}`;
                                return (
                                    <div className="searchResultRow" key={uniqueKey}>
                                        <Link
                                            to={path}
                                            onClick={onClickFn}
                                            className={handleCurrentLineAndGetClass(() => {
                                                onClickFn();
                                                history.push(path);
                                            })}
                                        >
                                            <div className="flex items-center gap-2">
                                                <ImageIconComponent
                                                    iconByType={
                                                        searchResult?.record_type?.toLowerCase() as FeRecordType
                                                    }
                                                    {...imageProps(
                                                        searchResult?.record_type?.toLowerCase() as FeRecordType,
                                                        searchResult?.id,
                                                        searchResult?.resolved_object_template_id
                                                    )}
                                                />
                                                <span className="recordName">
                                                    {searchResult?.name ?? searchResult?.id}
                                                </span>
                                                <CommentCountBadge count={searchResult?.comment_count} />
                                            </div>
                                        </Link>
                                        {searchResult?.name ? (
                                            <Tooltip overlay="Copy name">
                                                <IconButton
                                                    className="copyNameButton"
                                                    icon={IconCollection.contentCopy}
                                                    onClick={(e) =>
                                                        handleCopyName(e, searchResult?.name)
                                                    }
                                                />
                                            </Tooltip>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </>
            )}
            {recentSearches?.length > 0 && (
                <>
                    <span className="title text-xs text-uppercase">Recent searches</span>
                    {recentSearches.map((recentSearch) => {
                        const onClick = () => {
                            if (searchBarInputRef.current) {
                                searchBarInputRef.current.value = recentSearch;
                                dispatchSearch(recentSearch);
                                dispatchFullTextSearch(recentSearch);
                            }
                        };
                        return (
                            <button
                                key={recentSearch}
                                className={handleCurrentLineAndGetClass(onClick)}
                                type="button"
                                onClick={onClick}
                            >
                                <Icon icon={IconCollection.history} />
                                <span className="recordName">{recentSearch}</span>
                            </button>
                        );
                    })}
                </>
            )}
            <div className="advancedSearchContainer">
                <Button
                    onClick={() => openAdvancedSearch({ searchRecordType: false })}
                    variation="primary"
                    label="Advanced Search"
                />
                {recordTypeTerm.length ? (
                    <Button
                        onClick={() => openAdvancedSearch({ searchRecordType: true })}
                        variation="white"
                        label={`Search for all ${recordTypeTerm}s`}
                    />
                ) : null}
            </div>
        </Popover>
    );

    return (
        <div className="searchBar">
            <div className="searchIconSlot">
                {isLoading || isFullTextLoading ? (
                    <CircularProgress size={20} />
                ) : (
                    <Icon className="searchIcon" icon={IconCollection.magnify} />
                )}
            </div>

            <Whisper
                placement="bottom"
                trigger={["focus", "active"]}
                ref={popoverTriggerRef}
                speaker={resultsPanel}
                enterable
            >
                <div>
                    <Tooltip overlay="Ctrl+E" align={{ offset: [0, 20] }}>
                        <input
                            onChange={(e) => {
                                dispatchSearchDelayed(e.target.value);
                                dispatchFullTextSearchDelayed(e.target.value);
                            }}
                            onKeyDown={(event) => {
                                const callback = {
                                    Enter: () => {
                                        addRecentSearch(searchBarInputRef?.current?.value);
                                        openAdvancedSearch({ searchRecordType: false });
                                    },
                                    ArrowUp: () => {
                                        const newSelectedLine = selectedLine - 1;
                                        setSelectedLine(
                                            newSelectedLine < 1 ? totalLinesCount : newSelectedLine
                                        );
                                        openPopover();
                                    },
                                    ArrowDown: () => {
                                        const newSelectedLine = selectedLine + 1;
                                        setSelectedLine(
                                            newSelectedLine > totalLinesCount ? 1 : newSelectedLine
                                        );
                                        openPopover();
                                    },
                                }[event.key];
                                callback?.();
                            }}
                            type="search"
                            placeholder="Search..."
                            className="text-secondary text-base"
                            ref={searchBarInputRef}
                            onFocus={(e: any) => e.target.select()}
                        />
                    </Tooltip>
                </div>
            </Whisper>
        </div>
    );
};

export default SearchBar;
